import {
  IAircraft,
  IAircraftRowSetting,
  ICrewConnection,
  IFlight,
  IFlightAircraft,
  IFullFlight,
  IGroundEvent,
  IGroundTimeInterval,
  IImportFlight,
  unassignedAircraftId,
} from "@vmosolutions/oc-interfaces";
import { FlightGL } from "src/pages/FlightTimeline/gl/Flight/FlightGL";
import { TimelineGL } from "src/pages/FlightTimeline/gl/TimelineGL";
import { defaultAircraftRowSetting, getflightGLRowHeightSettings } from "src/pages/FlightTimeline/TimelineUtils";
import { Action, Type } from "../actions/flights/FlightTimeline.Actions";

/**
 * Function used to change the static values stored in the FlightGL component
 * Warning, this will modify the static values but anywhere that uses these would have to be re-mounted see new values.
 * @param ratio - percentage ratio, i.e 1.0, 1.5 etc
 */
const applyAircraftRowToFlightGL = (ratio = 1.0): void => {
  const aircraftRowSettings = getflightGLRowHeightSettings(ratio);
  FlightGL.rowHeightPercentage = ratio;
  FlightGL.aircraftRowHeight = aircraftRowSettings.aircraftRowHeight;
  FlightGL.aircraftRowPadding = aircraftRowSettings.aircraftRowPadding;
  FlightGL.actualDepartureStart = aircraftRowSettings.actualDepartureStart;
  FlightGL.actualDepartureHeight = aircraftRowSettings.actualDepartureHeight;
  FlightGL.scheduledStart = aircraftRowSettings.scheduledStart;
  FlightGL.scheduledHeight = aircraftRowSettings.scheduledHeight;
  FlightGL.aircraftRowTotalHeight = aircraftRowSettings.aircraftRowTotalHeight;
};

export const convertAircraftToFlightAircraft = (aircraft: IAircraft): IFlightAircraft => {
  const flightAircraft: IFlightAircraft = {
    _id: aircraft._id,
    registration: aircraft.registration,
    aircraftType: {
      _id: aircraft?.aircraftType?._id,
      title: aircraft?.aircraftType?.title,
      codeIATA: aircraft?.aircraftType?.codeIATA,
      codeICAO: aircraft?.aircraftType?.codeICAO,
    },
    serialNumber: aircraft.serialNumber,
    generateErrors: aircraft.generateErrors,
    requireMessages: aircraft.requireMessages,
    subCharter: aircraft.subCharter,
    planning: aircraft.planning,
    foreignRef: aircraft.foreignRef,
  };
  return flightAircraft;
};

export interface ITimelineEvents {
  flights: IFlight[];
  groundEvents: IGroundEvent[];
  lastProcessedTime: number | null;
}

export interface ITimeIntervalLoaded {
  [yearMonth: string]: {
    startOfInterval: number;
    endOfInterval: number;
  };
}

export interface IAircraftFlights {
  [key: string]: ITimelineEvents;
}

export type TimelineEvent = IFlight | IGroundEvent;

export const isFlight = (a: any): a is IFlight | IImportFlight => {
  return !!a && "flightNumber" in a;
};

export const isGroundEvent = (a: any): a is IGroundEvent => {
  return "eventType" in a && "actualStart" in a && "actualEnd" in a;
};

export const isImportFlight = (a: any): a is IImportFlight => {
  return "flightNumber" in a && a._id?.includes("temp");
};

export enum InteractiveState {
  None,
  Pan,
  Reassign,
  BulkReassign,
  ScrollBar,
  ScrollBarThumb,
  LockTimeline,
}

interface ITimelineInteractiveState {
  [key: string]: InteractiveState;
}

interface IEditFlightMap {
  [key: string]: {
    flight: IFullFlight;
    isDirty: boolean;
  };
}

export interface ISavedFlightDisplayOptions {
  aircraftFilter: string[];
  aircraftSort: IAircraftSortOption;
}

export interface IAircraftFilterOption {
  value: string | string[] | "all" | "none";
  label: string;
  count: number;
  group: "saved" | "types";
  item_id: string;
}

export interface IAircraftSortOption {
  value: string;
  key: string[];
  dir: (boolean | "asc" | "desc")[];
  label: string;
}

export interface IDaySelection {
  daySelected: Date;
  aircraftSelected: string;
}

export interface IFocusedEvents {
  events: (IFlight | IGroundEvent | IImportFlight)[];
  shouldFocus?: "none" | "first" | "last" | "all";
}

export interface ITimelineGLInstance {
  windowId: string;
  timelineGL: TimelineGL;
}

interface IFlightContextMenu {
  [key: string]: boolean;
}

interface ISelectedCrewConnections {
  [key: string]: ICrewConnection[];
}

interface IAircraftContextId {
  [key: string]: string | null;
}

interface IAircraftContextInitialTab {
  [key: string]: string;
}

/**
 * Aircraft quick filter (ENG-715) for one flight display window. Temporary, in-memory only.
 *
 * `aircraft` is a SNAPSHOT taken when the user applies the filter, seeded from the aircraft they had selected. It is
 * deliberately not a live view of the selection: while the filter is applied, clicking rows changes the selection
 * (highlight, bulk assignment) without rows vanishing from under the user. Re-applying takes a fresh snapshot.
 * null (or empty) means this window is not filtering.
 */
export interface IAircraftQuickFilterState {
  /** The aircraft this window is pinned to, or null when not filtering. */
  aircraft: string[] | null;
  /**
   * Custom vertical order set by dragging rows while the filter is applied ("free mode").
   * null means follow the normal aircraft sort.
   */
  order: string[] | null;
}

/** Keyed by flight display (mosaic) window id so each window filters independently, like interactiveState. */
export interface IAircraftQuickFilters {
  [windowId: string]: IAircraftQuickFilterState;
}

/** Aircraft selected in each window's aircraft column, keyed by window id. */
export interface IAircraftSelections {
  [windowId: string]: string[];
}

/** The day selected in each flight display window (the other half of bulk assignment mode), keyed by window id. */
export interface ISelectedDays {
  [windowId: string]: Date | null;
}

/** Stable empty selection so a `?? fallback` never hands components a fresh array reference. */
export const noAircraftSelected: string[] = Object.freeze([]) as string[];

/** Shared default for windows with no quick filter yet. Frozen so a selector fallback never churns references. */
export const defaultAircraftQuickFilter: IAircraftQuickFilterState = Object.freeze({
  aircraft: null,
  order: null,
}) as IAircraftQuickFilterState;

export interface IFlightTimelineState {
  flightData: IAircraftFlights | null;
  isFlightContextOpen: IFlightContextMenu;
  isAircraftContextOpen: IFlightContextMenu;
  aircraftContextId: IAircraftContextId;
  aircraftContextInitialTab: IAircraftContextInitialTab;
  interactiveState: ITimelineInteractiveState;
  numLoadedAircafts: number;
  numAircrafts: number;
  flightDataLoading: string[];
  timeIntervalLoaded: ITimeIntervalLoaded;
  editFlights: IEditFlightMap;
  hoveredFlight: IFlight | IGroundEvent | null;
  focusedEvents: IFocusedEvents;
  selectedEvents: (IFlight | IGroundEvent)[];
  crewConnections: ISelectedCrewConnections;
  hoveredGroundTimeInterval: IGroundTimeInterval;
  /**
   * Per window (ENG-715). Aircraft selection drives the row highlight, bulk assignment and the quick filter, and
   * belongs to the window it was made in: two flight displays side by side do not mirror each other.
   */
  selectedDay: ISelectedDays;
  selectedAircraft: IAircraftSelections;
  aircraftFilters: string[];
  aircraftSort: IAircraftSortOption | null;
  /** Aircraft quick filter (ENG-715), per flight display window id. See IAircraftQuickFilterState. */
  quickFilters: IAircraftQuickFilters;
  hoveredTime?: number | null;
  userSetScale?: number[] | null;
  aircraftRowSetting: IAircraftRowSetting;
  timelineGLs: ITimelineGLInstance[];
}

const defaultState: IFlightTimelineState = {
  flightData: {
    [unassignedAircraftId]: {
      flights: [],
      groundEvents: [],
      lastProcessedTime: null,
    },
  },
  interactiveState: {},
  isFlightContextOpen: {},
  isAircraftContextOpen: {},
  aircraftContextId: {},
  aircraftContextInitialTab: {},
  flightDataLoading: [],
  timeIntervalLoaded: {},
  numLoadedAircafts: 0,
  numAircrafts: 1,
  editFlights: {},
  hoveredFlight: null,
  focusedEvents: {
    events: [],
    shouldFocus: "none",
  },
  selectedEvents: [],
  selectedDay: {},
  crewConnections: {},
  hoveredGroundTimeInterval: {
    prevEvent: undefined,
    nextEvent: undefined,
  },
  selectedAircraft: {},
  aircraftFilters: [],
  aircraftSort: null,
  quickFilters: {},
  hoveredTime: null,
  userSetScale: null,
  aircraftRowSetting: defaultAircraftRowSetting,
  timelineGLs: [],
};

/**
 * Keeps the quick filter custom order consistent with the filtered aircraft: drops ids that are no longer in the
 * filter and collapses an empty order back to null (follow the normal sort).
 */
const pruneQuickFilterOrder = (order: string[] | null, filteredAircraftIds: string[] | null): string[] | null => {
  if (!order?.length || !filteredAircraftIds?.length) return null;
  const filtered = new Set(filteredAircraftIds);
  const pruned = order.filter((id) => filtered.has(id));
  return pruned.length > 0 ? pruned : null;
};

/** Reduces the quick filter actions for a single flight display window. */
const reduceAircraftQuickFilter = (
  current: IAircraftQuickFilterState = defaultAircraftQuickFilter,
  action: Action
): IAircraftQuickFilterState => {
  switch (action.type) {
    case Type.TIMELINE_APPLY_QUICK_FILTER: {
      const aircraft = Array.from(new Set(action.aircraftIds ?? []));
      // Applying an empty set would filter everything away, so treat it as not filtering at all.
      if (aircraft.length === 0) return defaultAircraftQuickFilter;
      return { aircraft, order: pruneQuickFilterOrder(current.order, aircraft) };
    }
    case Type.TIMELINE_CLEAR_QUICK_FILTER:
      // Keep any free-mode arrangement so it comes back if the user re-applies the filter.
      return current.order?.length ? { aircraft: null, order: current.order } : defaultAircraftQuickFilter;
    case Type.TIMELINE_SET_QUICK_FILTER_ORDER:
      return {
        ...current,
        order: pruneQuickFilterOrder(action.order ? Array.from(new Set(action.order)) : null, current.aircraft),
      };
    default:
      return current;
  }
};

export const FlightTimelineReducer = (
  state: IFlightTimelineState = defaultState,
  action: Action
): IFlightTimelineState => {
  switch (action.type) {
    case Type.TIMELINE_ADD_FLIGHT_DATA:
      return {
        ...state,
        flightData: action.flightData,
        numLoadedAircafts: state.numLoadedAircafts + 1,
      };
    case Type.TIMELINE_NUM_AIRCRAFTS:
      return { ...state, numAircrafts: action.numAircrafts };
    case Type.TIMELINE_SET_AIRCRAFT_FILTERS:
      return {
        ...state,
        aircraftFilters: action.aircraftFilters,
      };
    case Type.TIMELINE_SET_AIRCRAFT_SORT:
      return {
        ...state,
        aircraftSort: action.aircraftSort,
      };
    // Aircraft quick filter (ENG-715): a per-window snapshot, independent of later selection changes.
    // Applying hands the selection over to the filter and drops it, so the filtered rows are shown plainly and the
    // user starts from a clean slate to pick the next, narrower set. That also ends bulk assignment mode, which
    // cannot outlive the aircraft selection it was working on.
    case Type.TIMELINE_APPLY_QUICK_FILTER:
      return {
        ...state,
        quickFilters: {
          ...state.quickFilters,
          [action.windowId]: reduceAircraftQuickFilter(state.quickFilters?.[action.windowId], action),
        },
        selectedAircraft: { ...state.selectedAircraft, [action.windowId]: noAircraftSelected },
        selectedDay: { ...state.selectedDay, [action.windowId]: null },
      };
    case Type.TIMELINE_CLEAR_QUICK_FILTER:
    case Type.TIMELINE_SET_QUICK_FILTER_ORDER:
      return {
        ...state,
        quickFilters: {
          ...state.quickFilters,
          [action.windowId]: reduceAircraftQuickFilter(state.quickFilters?.[action.windowId], action),
        },
      };
    case Type.TIMELINE_ADD_EDIT_FLIGHT:
      const flightId = action.flightData._id;
      const currentFlight: IFullFlight = state.editFlights[flightId]?.flight;
      return {
        ...state,
        editFlights: {
          ...state.editFlights,
          // Modified to use flightData._id instead of windowId to facilitate removal.
          [action.flightData._id]: { flight: { ...currentFlight, ...action.flightData }, isDirty: false },
        },
      };
    case Type.TIMELINE_ISDIRTY_EDIT_FLIGHT:
      const curFlight: IFullFlight = state.editFlights[action.windowId]?.flight;
      return {
        ...state,
        editFlights: {
          ...state.editFlights,
          [action.windowId]: { flight: curFlight, isDirty: action.isDirty },
        },
      };
    case Type.TIMELINE_REMOVE_EDIT_FLIGHT:
      const tempState = {
        ...state,
        editFlights: {
          ...state.editFlights,
        },
      };
      delete tempState.editFlights[action.flightId];
      return tempState;
    case Type.TIMELINE_GOT_UPDATED_FLIGHT:
      return {
        ...state,
        flightData: { ...state.flightData, ...action.flightData },
      };
    case Type.TIMELINE_FLIGHT_DATA_LOADING:
      const currentInterval = [...state.flightDataLoading];
      if (action.loaded) {
        const index = currentInterval.indexOf(action.interval);
        if (index > -1) {
          currentInterval.splice(index, 1);
        }
      } else {
        currentInterval.push(action.interval);
      }
      return { ...state, flightDataLoading: currentInterval };

    case Type.TIMELINE_LOADED_DATA_INTERVAL:
      return { ...state, timeIntervalLoaded: { ...state.timeIntervalLoaded, ...action.interval } };
    case Type.TIMELINE_UPDATE_HOVERED_FLIGHT:
      //const hoveredFlight = action.hoveredFlight ? { ...action.hoveredFlight } : null;
      return { ...state, hoveredFlight: action.hoveredFlight };
    case Type.TIMELINE_HOVERED_GROUND_TIME_INTERVAL:
      const curInterval = state.hoveredGroundTimeInterval;
      if (
        curInterval.prevEvent?._id !== action.interval.prevEvent?._id ||
        curInterval.nextEvent?._id !== action.interval?.nextEvent?._id
      ) {
        return { ...state, hoveredGroundTimeInterval: action.interval };
      } else {
        return state;
      }
    case Type.TIMELINE_SET_CREW_CONNECTIONS:
      return {
        ...state,
        crewConnections: {
          ...state.crewConnections,
          [action.windowId]: action.connections,
        },
      };
    case Type.TIMELINE_SET_INTERACTIVE_STATE:
      return {
        ...state,
        interactiveState: {
          ...state.interactiveState,
          [action.windowId]: action.state,
        },
      };
    //return { ...state, interactiveState: action.state };
    case Type.TIMELINE_SET_IS_FLIGHT_CONTEXT_OPEN:
      return {
        ...state,
        isFlightContextOpen: {
          ...state.isFlightContextOpen,
          [action.windowId]: action.isOpen,
        },
      };
    case Type.TIMELINE_SET_SELECTED_EVENTS:
      return { ...state, selectedEvents: action.selectedEvents };
    case Type.TIMELINE_SET_FOCUSED_EVENTS:
      return { ...state, focusedEvents: { ...action.focusedEvents } };
    case Type.TIMELINE_GET_UNASSIGNED_AIRCRAFT_FLIGHTS:
      const unassignedFlights: { flights: any; groundEvents: IGroundEvent[]; lastProcessedTime: number } = {
        flights: action.unassignedFlights,
        groundEvents: [],
        lastProcessedTime: Date.now(),
      };
      const flightData = {
        [unassignedAircraftId]: {
          ...(state?.flightData && state.flightData[unassignedAircraftId]),
          ...unassignedFlights,
        },
      };
      return { ...state, flightData: { ...state?.flightData, ...flightData } };
    case Type.TIMELINE_SET_SELECTED_DAY:
      return { ...state, selectedDay: { ...state.selectedDay, [action.windowId]: action.selectedDay } };
    case Type.TIMELINE_SET_SELECTED_AIRCRAFT:
      return {
        ...state,
        selectedAircraft: { ...state.selectedAircraft, [action.windowId]: action.selectedAircraft },
      };
    case Type.TIMELINE_SET_BULK_ASSIGNMENT_MODE:
      return {
        ...state,
        selectedDay: { ...state.selectedDay, [action.windowId]: action.selectedDay },
        selectedAircraft: { ...state.selectedAircraft, [action.windowId]: action.selectedAircraft },
      };
    case Type.TIMELINE_UPDATE_HOVERED_TIME:
      const hoveredTime = action.hoveredTime ? action.hoveredTime : null;
      return { ...state, hoveredTime };
    case Type.TIMELINE_SET_USER_SET_SCALE:
      return { ...state, userSetScale: action.userSetScale };
    case Type.TIMELINE_AIRCRAFT_ROW_SETTINGS:
      applyAircraftRowToFlightGL(action.rowPercentage);
      const updatedAircraftRowSetting = getflightGLRowHeightSettings(action.rowPercentage);
      return { ...state, aircraftRowSetting: updatedAircraftRowSetting };
    case Type.TIMELINE_SET_TIMELINEGL:
      return { ...state, timelineGLs: action.timelineGLs };
    case Type.TIMELINE_SET_IS_AIRCRAFT_CONTEXT_OPEN:
      return {
        ...state,
        isAircraftContextOpen: {
          ...state.isAircraftContextOpen,
          [action.windowId]: action.isOpen,
        },
      };
    case Type.TIMELINE_SET_AIRCRAFT_CONTEXT_ID:
      return {
        ...state,
        aircraftContextId: {
          ...state.aircraftContextId,
          [action.windowId]: action.aircraftId,
        },
        aircraftContextInitialTab: {
          ...state.aircraftContextInitialTab,
          [action.windowId]: action.initialTab ?? "overview",
        },
      };
    default:
      return state;
  }
};
